import { useCallback, useMemo, useState } from 'react'
import '@inovua/reactdatagrid-community/index.css'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import './Table.scss'
import { ReactComponent as UpArrowIcon } from '../../../assets/arrow-narrow-up.svg'
import { ReactComponent as DownArrowIcon } from '../../../assets/arrow-narrow-down.svg'
import { ReactComponent as UpDownArrowIcon } from '../../../assets/arrow-down-up.svg'
import usePersistedState from '../../../hooks/usePersistedState'

/**
 * Custom editor for date fields. The react datagrid library does
 * have one but to use it you have to install momentjs which is a
 * 5MB library.
 * @param {*} params
 * @returns
 */
export const DateEditor = ({ value, onChange, onComplete }) => {
  const localValue = value ? new Date(value).toISOString().slice(0, 10) : ''

  return (
    <input
      type='date'
      value={localValue}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onComplete}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onComplete()
      }}
      // I prefer styled components! Also, this tries to make it look native to RDG.
      style={{
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        border: 'none',
        outline: 'none',
        fontSize: '14px',
        fontFamily: 'inherit',
        color: '#555e68',
        background: 'transparent',
        appearance: 'none' // remove native browser styling
      }}
    />
  )
}

const SortIndicator = ({ direction }) => {
  return (
    <div className='icon-wrapper'>
      {direction === 1 && <UpArrowIcon />}
      {direction === -1 && <DownArrowIcon />}
      {direction === null && <UpDownArrowIcon />}
    </div>
  )
}

const renderSortTool = (direction) => {
  return <SortIndicator direction={direction} />
}

/**
 *
 * @param {string | null} storageKey
 * @returns
 */
const useStoredTablePreferences = (storageKey) => {
  const tableStorageKey = storageKey !== null ? `${storageKey}-table` : null
  const [prefs, setPrefs] = usePersistedState(tableStorageKey, { sorting: undefined, columnOrder: undefined }, localStorage)

  const onSortInfoChange = useCallback(
    (sortInfo) => {
      if (!tableStorageKey) return
      setPrefs((prev) => ({ ...prev, sorting: sortInfo }))
    },
    [tableStorageKey, setPrefs]
  )

  const storeColumnOrder = useCallback(
    (columnOrder) => {
      if (!tableStorageKey) return
      setPrefs((prev) => ({ ...prev, columnOrder }))
    },
    [tableStorageKey, setPrefs]
  )

  return {
    onSortInfoChange,
    sortPrefs: prefs?.sorting,
    storeColumnOrder,
    defaultColumnOrder: prefs?.columnOrder
  }
}

export function Table({ columns = [], data = [], dataSource, idProperty, storageKey = null, defaultSortInfo, ...props }) {
  const { onSortInfoChange, sortPrefs, storeColumnOrder, defaultColumnOrder } = useStoredTablePreferences(storageKey)
  const [columnOrder, setColumnOrder] = useState(defaultColumnOrder)

  const onColumnOrderChange = (columnOrder) => {
    setColumnOrder(columnOrder)
    storeColumnOrder(columnOrder)
  }

  /**
   * Standardises the table input for our library.
   */
  const mappedColumns = useMemo(
    () =>
      columns.map(({ label, dataKey, cellRenderer, width, flexGrow, editable = false, ...rest }) => {
        return {
          name: dataKey,
          header: label,
          defaultFlex: flexGrow,
          editable,
          defaultWidth: width,
          sortable: label !== 'Actions',
          render: typeof cellRenderer === 'function' ? ({ data }) => cellRenderer({ dataKey, rowData: data }) : undefined,
          ...rest
        }
      }),
    [columns]
  )

  return (
    <div className='table-wrapper'>
      <ReactDataGrid
        className='rdg-table'
        columns={mappedColumns}
        idProperty={idProperty || mappedColumns[0]?.name || undefined}
        pagination={data.length > 15 || !!dataSource}
        dataSource={dataSource ? dataSource : data}
        defaultSortInfo={sortPrefs !== undefined ? sortPrefs : defaultSortInfo}
        showColumnMenuTool={false}
        renderSortTool={renderSortTool}
        onSortInfoChange={onSortInfoChange}
        onColumnOrderChange={onColumnOrderChange}
        columnOrder={columnOrder}
        {...props}
      />
    </div>
  )
}

export default Table
